// AddDrawer — right-side sliding drawer for adding any forecast input type.
// Shows a type-picker if no type is pre-selected; then renders the appropriate form.

import { useState } from "react";
import { X } from "lucide-react";
import type { ForecastInputRecord } from "@workspace/api-client-react";
import { AddAccrualForm, AddCommitmentForm, AddPlannedSpendForm } from "./add-forms";
import type { ForecastContext, InputType } from "./types";
import { TYPE_HELP } from "./types";

interface AddDrawerProps {
  ctx: ForecastContext;
  initialType: InputType | null;
  onSaved: (record: ForecastInputRecord, withRefresh: boolean) => void;
  onClose: () => void;
}

export function AddDrawer({ ctx, initialType, onSaved, onClose }: AddDrawerProps) {
  const [inputType, setInputType] = useState<InputType | null>(initialType);
  const typeInfo = inputType ? TYPE_HELP[inputType] : null;

  return (
    <div className="fi-drawer" role="dialog" aria-modal="true" aria-label={typeInfo?.title ?? "Add Forecast Input"} data-testid="fi-add-drawer">
      <div className="fi-drawer-head">
        <div>
          <div className="eyebrow">FORECAST INPUTS</div>
          <h3>{typeInfo?.title ?? "Add Forecast Input"}</h3>
          {typeInfo && <p className="fi-drawer-help">{typeInfo.help}</p>}
        </div>
        <button
          onClick={onClose}
          aria-label="Close add input drawer"
          data-testid="fi-add-drawer-close"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="fi-drawer-body">
        {!inputType && (
          <div className="fi-type-picker" data-testid="fi-type-picker">
            <p className="fi-type-picker-prompt" id="fi-type-picker-label">What are you adding?</p>
            {(["accrual", "commitment", "planned_spend"] as InputType[]).map(t => (
              <button
                key={t}
                className="fi-type-option"
                onClick={() => setInputType(t)}
                aria-describedby="fi-type-picker-label"
                data-testid={`fi-type-option-${t}`}
              >
                <div className="fi-type-option-label">
                  {t === "accrual" ? "Accrual" : t === "commitment" ? "Commitment" : "Planned Spend"}
                </div>
                <div className="fi-type-option-help">{TYPE_HELP[t].help}</div>
              </button>
            ))}
          </div>
        )}

        {inputType === "accrual" && (
          <AddAccrualForm ctx={ctx} onSaved={onSaved} onCancel={() => setInputType(null)} />
        )}
        {inputType === "commitment" && (
          <AddCommitmentForm ctx={ctx} onSaved={onSaved} onCancel={() => setInputType(null)} />
        )}
        {inputType === "planned_spend" && (
          <AddPlannedSpendForm ctx={ctx} onSaved={onSaved} onCancel={() => setInputType(null)} />
        )}
      </div>
    </div>
  );
}
